import { useEffect, useRef, useState } from 'react';
import { Pause, Play, SkipForward, Volume2, VolumeX } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Progress } from '@/components/ui/progress';

export default function RestTimer({ seconds = 60, onFinish, onSkip }) {
    const [remaining, setRemaining] = useState(seconds);
    const [running, setRunning] = useState(true);
    const [muted, setMuted] = useState(false);
    const startRef = useRef(Date.now());
    const totalRef = useRef(seconds);
    const intervalRef = useRef(null);

    useEffect(() => {
        // Reset when seconds change
        setRemaining(seconds);
        totalRef.current = seconds;
        startRef.current = Date.now();
        setRunning(true);
    }, [seconds]);

    useEffect(() => {
        if (!running) return;
        intervalRef.current = setInterval(() => {
            const elapsed = Math.floor((Date.now() - startRef.current) / 1000);
            const r = Math.max(0, totalRef.current - elapsed);
            setRemaining(r);
            if (r === 3 || r === 2 || r === 1) playTick();
            if (r <= 0) {
                clearInterval(intervalRef.current);
                playBeep();
                onFinish && onFinish();
            }
        }, 250);
        return () => clearInterval(intervalRef.current);
    }, [running, onFinish]); // eslint-disable-line

    const toggle = () => {
        if (running) {
            totalRef.current = remaining;
            setRunning(false);
        } else {
            startRef.current = Date.now();
            setRunning(true);
        }
    };

    const playBeep = () => {
        if (muted) return;
        try {
            const ctx = new (window.AudioContext || window.webkitAudioContext)();
            const osc = ctx.createOscillator();
            const gain = ctx.createGain();
            osc.frequency.value = 880;
            gain.gain.value = 0.08;
            osc.connect(gain);
            gain.connect(ctx.destination);
            osc.start();
            setTimeout(() => { osc.stop(); ctx.close(); }, 350);
        } catch (_) {}
    };
    const playTick = () => {
        if (muted) return;
        try {
            const ctx = new (window.AudioContext || window.webkitAudioContext)();
            const osc = ctx.createOscillator();
            const gain = ctx.createGain();
            osc.frequency.value = 520;
            gain.gain.value = 0.04;
            osc.connect(gain);
            gain.connect(ctx.destination);
            osc.start();
            setTimeout(() => { osc.stop(); ctx.close(); }, 120);
        } catch (_) {}
    };

    const pct = Math.round(((totalRef.current - remaining) / totalRef.current) * 100);
    const mm = String(Math.floor(remaining / 60)).padStart(2, '0');
    const ss = String(remaining % 60).padStart(2, '0');

    return (
        <div className="rounded-[var(--radius-lg)] bg-[hsl(var(--surface-2))] border border-border p-5 pulse-rest" data-testid="rest-timer">
            <div className="flex items-center justify-between mb-3">
                <div className="text-xs uppercase tracking-widest text-[hsl(var(--accent))] font-medium">Descanso</div>
                <button onClick={() => setMuted(!muted)} className="text-muted-foreground hover:text-foreground" data-testid="timer-mute-toggle" aria-label="mute">
                    {muted ? <VolumeX className="h-4 w-4" /> : <Volume2 className="h-4 w-4" />}
                </button>
            </div>
            <div className="text-5xl sm:text-6xl font-semibold tabular-nums text-center mb-4 font-display" data-testid="rest-timer-digits">{mm}:{ss}</div>
            <Progress value={pct} className="h-2 bg-[hsl(var(--surface-3))]" />
            <div className="flex items-center justify-center gap-2 mt-4">
                <Button size="sm" variant="secondary" onClick={toggle} data-testid={running ? 'timer-pause-button' : 'timer-start-button'}>
                    {running ? <Pause className="h-4 w-4 mr-1" /> : <Play className="h-4 w-4 mr-1" />}
                    {running ? 'Pausar' : 'Seguir'}
                </Button>
                <Button size="sm" variant="secondary" onClick={() => { clearInterval(intervalRef.current); onSkip && onSkip(); }} data-testid="timer-skip-button">
                    <SkipForward className="h-4 w-4 mr-1" /> Saltar descanso
                </Button>
            </div>
        </div>
    );
}
